import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';

export default function DashboardEnseignant() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ eleves: 0, presences: 0, moyenne: 0, coursAujourdhui: 0, alertes: 0, aCorreger: 0, difficultes: 0, termines: 0 });
  const [coursJour, setCoursJour] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [user, setUser] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(u);

    const lastName  = (u.lastName  || '').trim().toLowerCase();
    const firstName = (u.firstName || '').trim().toLowerCase();

    Promise.all([
      api.get('/catalog/teachers'),
      api.get(`/timetable?date=${today}`),
      api.get('/evaluations'),
    ]).then(async ([allTeachers, timetable, evals]) => {
      // Trouver l'ID catalog de cet enseignant
      const myTeacher = (allTeachers || []).find((t) =>
        (t.lastName  || '').trim().toLowerCase() === lastName &&
        (t.firstName || '').trim().toLowerCase() === firstName
      );
      const myTeacherId = myTeacher?.id;

      // Filtrer l'EDT par cet enseignant
      const allSessions = timetable || [];
      const mySessions = allSessions.filter((s) => {
        if (myTeacherId) return s.teacher?.id === myTeacherId;
        // fallback: comparaison par nom
        return (s.teacher?.lastName || '').trim().toLowerCase() === lastName;
      });

      // Classes uniques de cet enseignant
      const myClassIds = [...new Set(mySessions.map((s) => s.class?.id).filter(Boolean))];

      // Élèves de ces classes
      let myStudents = [];
      if (myClassIds.length > 0) {
        const results = await Promise.all(
          myClassIds.map((cid) =>
            api.get(`/students?classId=${cid}&limit=500`).catch(() => [])
          )
        );
        const flat = results.flatMap((r) => (Array.isArray(r) ? r : r?.data || r?.students || []));
        // dédoublonner
        const seen = new Set();
        myStudents = flat.filter((s) => { if (seen.has(s.id)) return false; seen.add(s.id); return true; });
      }

      // Évaluations de cet enseignant
      const myEvals = (evals || []).filter((e) => {
        if (myTeacherId) return e.teacher?.id === myTeacherId || e.teacherId === myTeacherId;
        return (e.teacher?.lastName || '').trim().toLowerCase() === lastName;
      });

      const notesToSaisir = myEvals.filter((e) => e.status === 'BROUILLON' || e.status === 'CORRIGE').length;
      const notes = myEvals.flatMap((e) =>
        (e.results || []).filter((r) => r.totalObtained != null).map((r) => r.totalObtained)
      );
      const moy = notes.length ? (notes.reduce((a, b) => a + b, 0) / notes.length).toFixed(1) : 0;

      setStats({
        eleves: myStudents.length,
        presences: 100,
        moyenne: moy,
        coursAujourdhui: mySessions.length,
        alertes: 0,
        aCorreger: notesToSaisir,
        difficultes: 0,
        termines: myEvals.filter((e) => e.status === 'PUBLIE' || e.status === 'CLOTURE').length,
      });
      setCoursJour(mySessions);
      setEvaluations(myEvals.slice(0, 5));
    }).catch(() => {});
  }, []);

  const prenom = user?.firstName || 'Enseignant';

  const KPIS_TOP = [
    { label: 'Mes Élèves', value: stats.eleves, sub: 'dans mes classes', color: '#3b82f6', bg: '#eff6ff', icon: '👨‍🎓' },
    { label: 'Présences', value: `${stats.presences}%`, sub: '0 absents', color: '#16a34a', bg: '#f0fdf4', icon: '✓' },
    { label: 'Moyenne Générale', value: `${stats.moyenne}/20`, sub: `${stats.eleves} notes saisies`, color: '#7c3aed', bg: '#faf5ff', icon: '📊' },
    { label: "Cours Aujourd'hui", value: stats.coursAujourdhui, sub: 'séances programmées', color: '#d97706', bg: '#fffbeb', icon: '📅' },
  ];

  const KPIS_BOT = [
    { label: 'Alertes', value: stats.alertes, sub: 'Élèves à suivre', color: '#dc2626', icon: '⚠' },
    { label: 'À corriger', value: stats.aCorreger, sub: 'Évaluations', color: '#d97706', icon: '📝' },
    { label: 'Difficultés', value: stats.difficultes, sub: 'Élèves < 10/20', color: '#7c3aed', icon: '📉' },
    { label: 'Terminés', value: stats.termines, sub: 'Leçons', color: '#16a34a', icon: '✅' },
  ];

  const ACTIONS = [
    { label: 'Mes Élèves', path: '/eleves', icon: '👨‍🎓' },
    { label: 'Saisir les Notes', path: '/evaluations', icon: '📝' },
    { label: 'Gérer Absences', path: '/presences', icon: '✓' },
    { label: "Emploi du Temps", path: '/emploi-du-temps', icon: '📅' },
    { label: 'Mes Classes', path: '/eleves', icon: '🏫' },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 600 }}>Tableau de bord <span style={{ color: 'var(--primary)' }}>Enseignant</span></h1>
          <p style={{ color: 'var(--warn)', fontSize: 13, marginTop: 2 }}>Bonjour {prenom} ! Voici un aperçu de votre journée et de vos classes</p>
        </div>
      </div>

      {/* KPIs top */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
        {KPIS_TOP.map((k) => (
          <div key={k.label} style={{ background: k.bg, border: `1px solid ${k.color}20`, borderRadius: 10, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 12, color: k.color, fontWeight: 500, marginBottom: 4 }}>{k.label}</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 700, color: k.color, lineHeight: 1 }}>{k.value}</div>
                <div style={{ fontSize: 11, color: k.color + '99', marginTop: 6 }}>{k.sub}</div>
              </div>
              <span style={{ fontSize: '1.8rem', opacity: 0.25 }}>{k.icon}</span>
            </div>
          </div>
        ))}
      </div>

      {/* KPIs secondaires */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {KPIS_BOT.map((k) => (
          <div key={k.label} className="card">
            <div style={{ padding: '12px 14px', textAlign: 'center' }}>
              <div style={{ color: k.color, fontSize: 12, marginBottom: 4 }}>⚡ {k.label}</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{k.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
        {/* EDT du jour */}
        <div className="card">
          <div className="card-head">
            <h2>Mes cours aujourd'hui</h2>
            <button className="btn-ghost btn-sm" onClick={() => navigate('/emploi-du-temps')}>Voir complet →</button>
          </div>
          <div className="card-body">
            {coursJour.length === 0 ? (
              <div className="empty">Aucun cours programmé aujourd'hui.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {coursJour.map((c) => (
                    <tr key={c.id} style={{ borderBottom: '0.5px solid var(--line)' }}>
                      <td style={{ padding: '10px 12px', color: 'var(--muted)', fontFamily: 'monospace', width: 90 }}>
                        {c.startTime}
                        <br /><span style={{ fontSize: 11 }}>Séance</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <b>{c.subject?.name || '—'}</b>
                        <br /><span style={{ fontSize: 11, color: 'var(--muted)' }}>{c.class?.name}</span>
                      </td>
                      {c.room && <td style={{ padding: '10px 12px', fontSize: 11 }}><span className="badge gray">📍 {c.room.name}</span></td>}
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <button className="btn-ghost btn-sm" onClick={() => navigate('/presences')}>Marquer absence</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Actions rapides */}
        <div className="card">
          <div className="card-head"><h2>⚡ Actions Rapides</h2></div>
          <div className="card-body" style={{ padding: 6 }}>
            {ACTIONS.map((a) => (
              <button key={a.label} className="btn-ghost"
                style={{ width: '100%', textAlign: 'left', justifyContent: 'space-between', marginBottom: 4, padding: '9px 12px' }}
                onClick={() => navigate(a.path)}>
                <span>{a.icon} {a.label}</span>
                <span style={{ color: 'var(--muted)' }}>›</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
